import { useState, useEffect } from 'react';
import { Dialog, Input, Select, Button, Badge } from '../design-system/components';

/**
 * Clone modal component for denied requests
 * Allows creating a new request with modified details
 */
export const CloneModal = ({
  cloningRequest,
  venues,
  onSubmit,
  onCancel
}) => {
  const [formData, setFormData] = useState({
    date: '',
    time: '',
    venue: '',
    fieldSize: '',
    ageGroup: '',
    notes: ''
  });

  useEffect(() => {
    if (cloningRequest) {
      // Normalize date from MM/DD/YYYY to YYYY-MM-DD for input[type="date"]
      const requestedDate = cloningRequest['Requested Date'] || '';
      const parts = requestedDate.split('/');
      const normalizedDate = parts.length === 3 ? `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}` : '';

      setFormData({
        date: normalizedDate,
        time: cloningRequest['Preferred Time'] || '',
        venue: cloningRequest['Requested Venue'] || '',
        fieldSize: cloningRequest['Field Size'] || '',
        ageGroup: cloningRequest['Age Group'] || '',
        notes: cloningRequest['Admin Notes'] ? `Note: ${cloningRequest['Admin Notes']}` : ''
      });
    }
  }, [cloningRequest]);

  const handleSubmit = () => {
    // Convert date back to MM/DD/YYYY format
    const parts = formData.date.split('-');
    const formattedDate = parts.length === 3 ? `${parts[1]}/${parts[2]}/${parts[0]}` : cloningRequest['Requested Date'];

    const updatedData = {
      ...cloningRequest,
      'Requested Date': formattedDate,
      'Preferred Time': formData.time,
      'Requested Venue': formData.venue,
      'Field Size': formData.fieldSize,
      'Age Group': formData.ageGroup,
      'Admin Notes': formData.notes
    };

    onSubmit(updatedData);
  };

  if (!cloningRequest) return null;

  return (
    <Dialog
      open={!!cloningRequest}
      onClose={onCancel}
      width={560}
      title={
        <span className="flex items-center gap-2">
          Clone & revise request
          <Badge tone="info" variant="soft">Orig: #{cloningRequest['Game Number'] || 'N/A'}</Badge>
        </span>
      }
      footer={
        <>
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit}>Create New Request</Button>
        </>
      }
    >
      <p className="mb-4" style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
        Modify details below to create a new "Requested" entry.
      </p>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Date"
            type="date"
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
          />
          <Input
            label="Time"
            type="text"
            value={formData.time}
            onChange={(e) => setFormData({ ...formData, time: e.target.value })}
            placeholder="e.g. 10:00 AM"
          />
        </div>

        <Select
          label="Venue"
          value={formData.venue}
          onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
        >
          {venues.map(v => (
            <option key={v} value={v}>{v}</option>
          ))}
        </Select>

        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Field Size"
            value={formData.fieldSize}
            onChange={(e) => setFormData({ ...formData, fieldSize: e.target.value })}
          >
            <option value="11v11">11v11</option>
            <option value="9v9">9v9</option>
            <option value="7v7">7v7</option>
            <option value="Small Sided">Small Sided</option>
          </Select>
          <Input
            label="Age Group"
            type="text"
            value={formData.ageGroup}
            onChange={(e) => setFormData({ ...formData, ageGroup: e.target.value })}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label style={{ fontFamily: 'var(--font-sans)', fontWeight: 'var(--fw-semibold)', fontSize: 'var(--text-sm)', color: 'var(--text-strong)' }}>
            Admin Revision Note
          </label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Why are you cloning this? (e.g., Original time unavailable)"
            className="h-20"
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-base)',
              fontWeight: 'var(--fw-medium)',
              color: 'var(--text-strong)',
              background: 'var(--surface-card)',
              border: '2px solid var(--border-strong)',
              borderRadius: 'var(--radius-md)',
              padding: '11px 13px',
              resize: 'vertical',
            }}
          />
        </div>
      </div>
    </Dialog>
  );
};
